// GeojsonGroupedControl.tsx
import { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet-groupedlayercontrol";
import "leaflet-groupedlayercontrol/dist/leaflet.groupedlayercontrol.min.css";

type Item = {
  group: string;
  name: string;
  url: string;
  defaultOn?: boolean;
  order?: number; // lower = below, and earlier in the list
  iconUrl?: string;
  iconSize?: [number, number];
  iconAnchor?: [number, number];
  popupAnchor?: [number, number];
  iconClassName?: string;
};

export default function GeojsonGroupedControl({ items }: { items: Item[] }) {
  const map = useMap();

  useEffect(() => {
    // 1) Reuse a single control
    let ctrl = (map as any)._groupedCtrl as any;
    if (!ctrl) {
      ctrl = (L.control as any).groupedLayers(
        {},
        {},
        {
          collapsed: true,
          groupCheckboxes: true,
          autoZIndex: false,
          sortLayers: true,
          exclusiveGroups: ["Top 1000 Cities (Oxford Economics)", "Rasters"],
          sortFunction: (
            layerA: any,
            layerB: any,
            nameA: string,
            nameB: string
          ) => {
            const a = layerA?._desiredOrder ?? 0;
            const b = layerB?._desiredOrder ?? 0;
            return a === b ? nameA.localeCompare(nameB) : a - b;
          },
        }
      );
      ctrl.addTo(map);
      (map as any)._groupedCtrl = ctrl;

      // Add "None" ONCE for the Rasters group
      const noneRaster = L.layerGroup();
      ctrl.addOverlay(noneRaster, "None", "Rasters");
      noneRaster.addTo(map);

      // registry to avoid duplicate entries
      (map as any)._overlayRegistry = new Set<string>();
    }

    const reg: Set<string> =
      (map as any)._overlayRegistry ||
      ((map as any)._overlayRegistry = new Set());

    const abort = new AbortController();
    const created: L.Layer[] = [];
    let disposed = false;

    const ordered = [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const baseZ = 410,
      stepZ = 5;

    const mkPaneName = (g: string, n: string) =>
      `pane-${g}-${n}`
        .replace(/\s+/g, "-")
        .replace(/[^-\w]/g, "")
        .toLowerCase();

    const colorFor = (v: any) => {
      const n = Math.max(1, Math.min(1000, Number(v) || 1000));
      const i = Math.min(9, Math.floor((n - 1) / 100));
      return [
        "#16a34a",
        "#22c55e",
        "#84cc16",
        "#a3e635",
        "#eab308",
        "#f59e0b",
        "#f97316",
        "#ef4444",
        "#dc2626",
        "#991b1b",
      ][i];
    };

    const buildLayer = async (item: Item, idx: number) => {
      const res = await fetch(item.url, { signal: abort.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status} for ${item.url}`);
      const fc = (await res.json()) as GeoJSON.FeatureCollection;

      const pane = mkPaneName(item.group, item.name);
      if (!map.getPane(pane)) {
        const p = map.createPane(pane);
        p.style.zIndex = String(baseZ + idx * stepZ);
      }

      const icon = L.icon({
        iconUrl: item.iconUrl ?? "/icons/pin2_r.svg",
        iconSize: item.iconSize ?? [28, 35],
        iconAnchor: item.iconAnchor ?? [14, 28],
        popupAnchor: item.popupAnchor ?? [0, -24],
        className: item.iconClassName ?? "",
      });

      const layer = L.geoJSON(fc, {
        pane,
        pointToLayer: (_f, latlng) => L.marker(latlng, { icon, pane }),
        onEachFeature: (feature, lyr) => {
          const p = feature.properties as any;
          const title = [p?.City, p?.Country].filter(Boolean).join(", ");
          const badge = (label: string, val: any) =>
            `<div class="score-row"><div class="score-label">${label}</div><div class="score-badge" style="background:${colorFor(
              val
            )};">${val ?? "—"}</div></div>`;
          const html = `
            <div class="popup-wrap">
              <div class="popup-title">${title || "Place"}</div>
              
              ${badge("Governance", p?.Governance)}
               ${badge("Quality of Life", p?.Quality_of_Life)}
               ${badge("Environment", p?.Environment)}
              ${badge("Human Capital", p?.Human_Capital)}
              ${badge("Economics", p?.Economics)}
            </div>`;
          (lyr as L.Layer).bindPopup(html, {
            autoPan: false,
            closeButton: false,
            className: "city-popup",
          });
          lyr.on("mouseover", () => (lyr as any).openPopup());
          lyr.on("mouseout", () => (lyr as any).closePopup());
        },
      });

      // Tag with desired order so the control's sortFunction is deterministic
      (layer as any)._desiredOrder = idx;

      return { item, idx, layer };
    };

    (async () => {
      try {
        const built = await Promise.all(
          ordered.map((it, i) => buildLayer(it, i))
        );
        if (disposed) return;

        built
          .sort((a, b) => a.idx - b.idx)
          .forEach(({ item, layer }) => {
            const key = `${item.group}::${item.name}`;
            if (!reg.has(key)) {
              ctrl.addOverlay(layer, item.name, item.group);
              reg.add(key);
            }
            if (item.defaultOn) layer.addTo(map);
            created.push(layer);
          });
      } catch (e: any) {
        if (e?.name !== "AbortError") console.error(e);
      }
    })();

    return () => {
      disposed = true;
      abort.abort();
      // IMPORTANT: do NOT remove the grouped control here
      created.forEach((l) => {
        try {
          map.removeLayer(l);
        } catch {}
      });
    };
  }, [map, items]);
  return null;
}
